import { FunctionalComponent } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import { Tag } from "../utils/api";

interface TagFilterProps {
  tags: Tag[];
  selectedTag: string | null;
  onSelectTag: (tag: string | null) => void;
}

export const TagFilter: FunctionalComponent<TagFilterProps> = ({
  tags,
  selectedTag,
  onSelectTag,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedTagObj = tags.find((t) => t.name === selectedTag);

  return (
    <div class="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        class={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
          selectedTag
            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 shadow-sm"
            : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/50"
        }`}
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
        {selectedTagObj ? selectedTagObj.name : "Filter by Tag"}
        {selectedTag && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onSelectTag(null);
              setIsOpen(false);
            }}
            class="ml-1 px-1.5 py-0.5 text-xs rounded-full hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
          >
            ×
          </span>
        )}
        <svg class="h-3 w-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div class="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-stone-900 rounded-xl shadow-xl border border-stone-200 dark:border-stone-800 z-50 max-h-80 overflow-y-auto py-1">
          <button
            onClick={() => {
              onSelectTag(null);
              setIsOpen(false);
            }}
            class="w-full px-4 py-2.5 text-left text-sm hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
          >
            All Tags
          </button>
          {tags.map((tag) => (
            <button
              key={tag.id}
              onClick={() => {
                onSelectTag(tag.name);
                setIsOpen(false);
              }}
              class={`w-full px-4 py-2.5 text-left text-sm hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors flex items-center gap-2 ${
                selectedTag === tag.name
                  ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400"
                  : "text-stone-600 dark:text-stone-400"
              }`}
            >
              <span class={`w-2 h-2 rounded-full ${selectedTag === tag.name ? 'bg-primary-500' : 'bg-stone-300 dark:bg-stone-600'}`}></span>
              {tag.name}
            </button>
          ))}
          {tags.length === 0 && (
            <div class="px-4 py-3 text-sm text-stone-500 dark:text-stone-400 italic">
              No tags yet
            </div>
          )}
        </div>
      )}
    </div>
  );
};
